import { useTheme } from '@mui/material';
import { VISUAL_REFRESH } from 'common/utils';

/**
 * Read the refresh flag from the ambient theme, falling back to the session-wide constant.
 *
 * The fallback is what every path in the shipped app takes: nothing passes a value, so this resolves
 * to `VISUAL_REFRESH` exactly as a direct read would. What the inheritance buys is Storybook, where
 * `ThemeDecorator` builds a theme from the toolbar global but cannot rewrite a module constant — so
 * a component that wraps ITSELF in a provider (the live-room panels do) would otherwise rebuild the
 * theme from the constant and silently ignore the toolbar.
 *
 * Icons (HNS-262) are the first consumer where reading the constant would be actively WRONG rather
 * than merely equivalent. `Buttons/Button.tsx` gets away with a direct read because a story that
 * renders it also re-themes it; an icon is a leaf that only ever sees the ambient theme, so with a
 * direct read every Chromatic snapshot taken with "Visual refresh: on" would still show Material
 * glyphs — the exact mismatch this hook exists to prevent, on the one surface where it is invisible
 * at review time.
 *
 * Lives here rather than beside `withTheme` — its original home — so `common/theme/utils` can use it
 * without closing a cycle: `withTheme` imports `common/theme`, so the dependency has to run in that
 * direction. It needs nothing from the theme package itself, only MUI's context.
 *
 * `Theme['visualRefresh']` is typed as always present because every theme this app builds sets it,
 * but a component rendered outside any Natter provider gets MUI's default theme, where it genuinely
 * is `undefined` — hence the coalesce.
 */
export const useInheritedVisualRefresh = (): boolean => useTheme().visualRefresh ?? VISUAL_REFRESH;
